/**
 * My QC Task — workflows, batches, assigned requests (MT-QC-TASK-001).
 */
import { qcAxios } from '../../api/axiosQcClient';

const API_PREFIX = '/api';

/** Backend view scope for user-specific task lists. */
const VIEW = 'MY-TASK';

function normalizeWorkflowList(payload) {
  const items = payload?.data;
  if (!Array.isArray(items)) return [];
  return items.filter((item) => Boolean(item?.workflow_id));
}

function normalizeBatchList(payload) {
  const items = payload?.data?.data;
  if (!Array.isArray(items)) return [];
  return items.filter((item) => Boolean(item?.batch_id));
}

/**
 * Normalize `/api/assignmentData/getAssignedRequests` response shape.
 * Exported for reuse by other assigned-request services/pages.
 */
export function normalizeAssignments(payload) {
  const rows = payload?.data?.data;
  const pagination = payload?.data?.paginationData ?? {};
  return {
    rows: Array.isArray(rows) ? rows : [],
    totalCount: Number(pagination?.totalCount ?? 0) || 0,
    page: Number(pagination?.page ?? 1) || 1,
    limit: Number(pagination?.limit ?? 20) || 20,
    totalPages: Number(pagination?.totalPages ?? 0) || 0,
  };
}

export async function getUserWorkflows(reqStatus) {
  const { data } = await qcAxios.get(`${API_PREFIX}/wf/getUserWorkflows`, {
    params: {
      view: VIEW,
      req_status: reqStatus,
    },
  });
  return normalizeWorkflowList(data);
}

/**
 * Returns full workflow objects (including workflow_type, name) for workflows
 * the current user has assignments in. Uses getAllWorkflows with userSpecific=true
 * because getUserWorkflows only returns workflow_id + batch count.
 */
export async function getUserWorkflowsFull(reqStatus) {
  const { data } = await qcAxios.get(`${API_PREFIX}/wf/getAllWorkflows`, {
    params: { userSpecific: true, reqStatus },
  });
  const items = data?.data;
  return Array.isArray(items) ? items.filter((item) => Boolean(item?.workflow_id)) : [];
}

export async function getUserBatches({ workflowId, reqStatus }) {
  const { data } = await qcAxios.get(`${API_PREFIX}/batchData/getUserBatches`, {
    params: {
      workflow_id: workflowId,
      req_status: reqStatus,
      view: VIEW,
    },
  });
  return normalizeBatchList(data);
}

export async function getAssignedRequests({
  reqStatus,
  page,
  limit,
  batchId,
  workflowId,
  filters,
  sortBy,
  sortDirection,
}) {
  const { data } = await qcAxios.get(
    `${API_PREFIX}/assignmentData/getAssignedRequests`,
    {
      params: {
        reqStatus,
        userSpecific: true,
        reviewer: false,
        sortDirection: sortDirection ?? 'ASC',
        page,
        limit,
        batchId,
        workflowId,
        sortBy: sortBy ?? 'file_name',
        language: filters?.language ?? '',
        district: filters?.district ?? '',
        state: filters?.state ?? '',
        duration: filters?.duration ?? '',
        file_name: filters?.file_name ?? '',
        assigned_to: filters?.assigned_to ?? '',
        assigned_reviewer: filters?.assigned_reviewer ?? '',
        search: filters?.search ?? '',
      },
    },
  );
  return normalizeAssignments(data);
}
